/**
 * USER-PROFILE-1 — My Account.
 *
 * The signed-in person's own identity, and the page the account menu's "My
 * Account" row has been promising. It is deliberately NOT /firm-profile: that
 * page is the FIRM's legal identity (legal name, licence number, CPA of
 * record), one per firm, shared by everyone in it. This one is one per person.
 *
 * The split runs through the layout. What is yours to set — name, title, phone
 * — is a form. What is a fact of your login — email, firm, role — is text, not
 * a disabled input: a greyed-out box still reads as "a field you might be able
 * to fill in", and none of these three are. Your firm is not a preference; it
 * is who provisioned you. Your role is not a preference; it is what you are
 * allowed to do, granted by an administrator on the admin users page.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiClient } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { useAuth } from '@/contexts/AuthContext'
import { ACCOUNT_MENU_FIRM_PROFILE_ROLES, ROLE_LABELS } from '@/components/AppShell'
import { userInitials, AVATAR_BG, AVATAR_FG } from '@/lib/userInitials'
// Shared with the firm-profile page on purpose: both surfaces answer a failed
// save with `detail: string[]`, and one helper means one error rendering.
import { extractErrors } from './firmProfile/model'

interface AccountResponse {
  email: string
  name: string
  title: string | null
  phone: string | null
  role: string
  firm_name: string
  sso_only: boolean
  /** False when this sign-in has no `users` row — nothing to save to. */
  editable: boolean
}

interface FormState {
  name: string
  title: string
  phone: string
}

const EMPTY_FORM: FormState = { name: '', title: '', phone: '' }

function toForm(account: AccountResponse): FormState {
  return {
    name: account.name ?? '',
    title: account.title ?? '',
    phone: account.phone ?? '',
  }
}

/** A fact of the login, rendered as a fact. See the module note on why not an input. */
function ReadOnlyFact({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="text-sm font-medium">{value || '—'}</p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

export function AccountPage() {
  const { user, refreshUser } = useAuth()
  const [account, setAccount] = useState<AccountResponse | null>(null)
  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<string[]>([])
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    apiClient.get<AccountResponse>('/v1/account')
      .then(res => {
        setAccount(res.data)
        setForm(toForm(res.data))
      })
      .catch(err => setErrors(extractErrors(err)))
      .finally(() => setLoading(false))
  }, [])

  const update = (field: keyof FormState, value: string) =>
    setForm(prev => ({ ...prev, [field]: value }))

  async function handleSave() {
    setSaving(true)
    setErrors([])
    setNotice(null)
    try {
      const res = await apiClient.patch<AccountResponse>('/v1/account', form)
      setAccount(res.data)
      setForm(toForm(res.data))
      // The header avatar and menu print this name. Re-read /me so the change
      // is visible where the user just came from, not only on this page.
      await refreshUser()
      setNotice('Your account details have been saved.')
    } catch (err) {
      setErrors(extractErrors(err))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading your account…</div>
  }

  // Fall back to the auth context if the account call failed outright, so the
  // page still says who you are rather than rendering an empty shell.
  const role = account?.role || user?.role || ''
  const roleLabel = role ? (ROLE_LABELS[role] ?? role) : '—'
  const isPlatformAdmin = role === 'super_admin'
  const canManageFirm = !!role && ACCOUNT_MENU_FIRM_PROFILE_ROLES.includes(role)
  const email = account?.email || user?.email || ''
  const editable = account?.editable ?? false
  const ssoOnly = account?.sso_only ?? user?.sso_only ?? false

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-xl font-semibold">My Account</h1>
        <p className="text-sm text-muted-foreground">
          Your personal details. Your firm's legal identity — the name and licence that
          appear on client-facing reports — lives on the Firm Profile page.
        </p>
      </div>

      {errors.length > 0 && (
        <Alert variant="destructive">
          <AlertDescription>
            {errors.length === 1 ? (
              errors[0]
            ) : (
              <ul className="list-disc pl-4 space-y-0.5">
                {errors.map(e => <li key={e}>{e}</li>)}
              </ul>
            )}
          </AlertDescription>
        </Alert>
      )}

      {notice && (
        <Alert>
          <AlertDescription>{notice}</AlertDescription>
        </Alert>
      )}

      {!editable && !loading && (
        <Alert>
          <AlertDescription>
            This sign-in has no stored user record, so these details cannot be edited here.
            They are shown as your sign-in reports them.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle>Your account</CardTitle>
            <CardDescription>How you appear to your colleagues in this workspace.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12">
                <AvatarFallback
                  data-testid="account-avatar"
                  className="text-sm font-bold"
                  style={{ backgroundColor: AVATAR_BG, color: AVATAR_FG }}
                >
                  {userInitials(form.name || account?.name)}
                </AvatarFallback>
              </Avatar>
              <p className="text-xs text-muted-foreground">
                Your initials are taken from your name — there is nothing to upload.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="account_name">Full name</Label>
              <Input
                id="account_name"
                value={form.name}
                onChange={e => update('name', e.target.value)}
                disabled={!editable}
                placeholder="Jordan Reyes"
              />
            </div>

            <ReadOnlyFact
              label="Email"
              value={email}
              hint="This is your sign-in address. Changing it is an account-administration step, not a form field."
            />

            <div className="space-y-1.5">
              <Label htmlFor="account_title">Title</Label>
              <Input
                id="account_title"
                value={form.title}
                onChange={e => update('title', e.target.value)}
                disabled={!editable}
                placeholder="Managing Partner, CPA"
                maxLength={100}
              />
              <p className="text-xs text-muted-foreground">Optional.</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="account_phone">Phone</Label>
              <Input
                id="account_phone"
                value={form.phone}
                onChange={e => update('phone', e.target.value)}
                disabled={!editable}
                placeholder="+1 (555) 123-4567"
                maxLength={40}
              />
              <p className="text-xs text-muted-foreground">Optional.</p>
            </div>

            <Button onClick={handleSave} disabled={saving || !editable}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Your firm &amp; access</CardTitle>
              <CardDescription>
                Set when your account was provisioned. Shown here so you can see it, not change it.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isPlatformAdmin ? (
                // A platform admin is not a member of one firm. Printing some
                // firm's name here as "your firm" would be a straightforward
                // falsehood — the same one ACCOUNT-MENU-1 removed from the menu.
                <ReadOnlyFact
                  label="Firm"
                  value="Platform administration"
                  hint="Your account is platform-level and is not a member of a single firm."
                />
              ) : (
                <ReadOnlyFact
                  label="Firm"
                  value={account?.firm_name || user?.firm || ''}
                  hint="The firm you are signed in to."
                />
              )}

              <ReadOnlyFact
                label="Your role"
                value={roleLabel}
                hint="Roles are granted by an administrator."
              />

              {canManageFirm && (
                <Link
                  to="/firm-profile"
                  className="inline-block text-sm font-medium text-primary hover:underline"
                >
                  Manage firm details →
                </Link>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Security</CardTitle>
              <CardDescription>Handled by the sign-in flow, not by this form.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {ssoOnly ? (
                <p className="text-sm text-muted-foreground">
                  You sign in with your organisation's identity provider, so there is no
                  password to change here.
                </p>
              ) : (
                <>
                  <Link
                    to="/forgot-password"
                    className="inline-block text-sm font-medium text-primary hover:underline"
                  >
                    Change password →
                  </Link>
                  <p className="text-xs text-muted-foreground">
                    Sends a reset link to {email || 'your sign-in address'}. Passwords are never
                    entered or displayed on this page.
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
